import React from 'react';
import classNames from 'classnames';

import Store from 'store';
import Data from 'data';

import './Paperdoll.scss';
import ItemImage from 'components/ItemImage';
import { D2ItemTooltip } from 'tooltips';
import EditingContext from 'store/editingContext';
import { isSubType, itemSlotGroups, otherHandSlot, presetItems } from 'logic/itemTypes';
import { inventoryFindItem, inventoryHasDuplicate } from 'logic/inventory';
import { useDragDrop, useItemMover, useItemValidator } from 'components/dragDrop';
import { chainedRef } from 'utils';
import { useDragDropManager, useDrop } from 'react-dnd';
import { ClassSelect, MercSelect } from './ClassSelect';
import { gemStats, itemGetTypes, itemLevelReq } from 'logic/item';
import { selectStats } from 'logic/stats';
import AutoSave from './AutoSave';
import { DragEffectContext } from 'ui/dragdrop';
import { EmbedProfileLink, useContentCreator, useProfileSetId } from 'components/cclink';
import GameContext from 'gamectx';
import alignElement from 'ui/utils/alignElement';
import Overlay from 'ui/utils/Overlay';
import { hirelingIsClass } from 'logic/stateReducers';

// green 24 100 8
// blue 24 0 104
// red 144 0 0

function getDropResult(monitor, paperdollRef) {
  const result = monitor.getDropResult();
  if (result) return result;

  const offset = monitor.getClientOffset();
  if (!offset) return null;
  const box = paperdollRef.current.getBoundingClientRect();
  if (offset.x < box.left || offset.x >= box.right || offset.y < box.top || offset.y >= box.bottom) {
    return {location: "library"};
  }
}

export function isWrongOffhand(item, other) {
  const type1 = Data.itemTypes[Data.items[item?.base]?.type];
  return type1?.shoots && !isSubType(Data.items[other?.base]?.type, type1.shoots);
}

export function isItemUnusable(item, state, merc) {
  const profile = state.profiles[state.activeProfile];
  if (itemLevelReq(item, state, merc) > (merc ? profile.mercLevel : profile.level)) return true;
  const classReq = Data.itemTypes[Data.items[item.base]?.type]?.class;
  if (classReq && (merc || profile.class !== classReq) &&
    !(merc && hirelingIsClass(Data.hireling[profile.merc]?.[0]?.class, classReq))) return true;

  const base = Data.items[item.base];
  if (base) {
    const stats = selectStats(state)[merc ? 'mercenary' : 'player'];
    let {reqdex, reqstr} = base;
    let item_req_percent = item.stats?.item_req_percent || 0;
    if (item.socketedItems) {
      const types = itemGetTypes(item.base);
      for (const id of item.socketedItems) {
        const sock = (Data.gems[id] ? gemStats({}, id, types) : state.items[id]?.stats);
        if (sock?.item_req_percent) {
          item_req_percent += sock.item_req_percent;
        }
      }
    }
    if (item_req_percent) {
      if (reqdex) reqdex += (reqdex * (item_req_percent || 0) / 100) | 0;
      if (reqstr) reqstr += (reqstr * (item_req_percent || 0) / 100) | 0;
    }
    if (item.ethereal) {
      if (reqdex) reqdex -= 10;
      if (reqstr) reqstr -= 10;
    }
    return stats && ((reqstr && stats.base.strength < reqstr) || (reqdex && stats.base.dexterity < reqdex));
  }

  return false;
}

function ItemSlot({ id, active, paperdollRef }) {
  const [hovering, setHovering] = React.useState(false);
  const [hlMerc, hlSlots] = EditingContext.useStateView("highlightItems") || [];
  const dragEffect = React.useContext(DragEffectContext);
  const hd = React.useContext(GameContext).gfx.hd;

  const {slot, merc} = itemSlotGroups[id];

  let specialHl = false;
  const origSlot = (slot === 'larm2' ? 'larm' : (slot === 'rarm2' ? 'rarm' : slot));
  if (hlSlots && hlSlots.has(origSlot) && !merc === !hlMerc) {
    active = true;
    specialHl = true;
  }

  const editDispatch = EditingContext.useDispatch();
  const handlers = {
    onMouseEnter: React.useCallback(() => setHovering(true), []),
    onMouseLeave: React.useCallback(() => setHovering(false), []),
    onClick: React.useCallback(() => editDispatch({type: "slot", slot: id}), [editDispatch, id]),
  };

  const itemId = Store.useProfile(profile => merc ? profile.mercItems[slot] : profile.items[slot]);
  const item = Store.useItem(itemId) || presetItems[itemId];

  const otherHand = otherHandSlot[slot];
  const otherId = Store.useProfile(profile => otherHand && (merc ? profile.mercItems[otherHand] : profile.items[otherHand]));
  const otherItem = Store.useItem(otherId) || presetItems[otherId];

  const offhandArt = !item && Data.items[otherItem?.base]?.['2handed'];
  const brokenItem = Store.useSelector(state => {
    if (!item) return false;
    if (!merc && isWrongOffhand(item, otherItem)) return true;
    return isItemUnusable(item, state, merc);
  }, [item, otherItem, merc]);

  const itemValidator = useItemValidator(slot, merc);
  const itemMover = useItemMover();

  const [dragClass, drag, drop] = useDragDrop("ITEM", {
    canDrag: () => itemId != null,
    item: () => ({type: "ITEM", id: itemId}),
    end: (item, monitor) => itemMover({location: merc ? "mercenary" : "equipment", slot}, getDropResult(monitor, paperdollRef)),
  }, {
    canDrop: item => item.type === "ITEM" && itemValidator(item.id),
    drop: () => ({location: merc ? "mercenary" : "equipment", slot}),
  }, [slot, merc, itemId, itemMover, itemValidator]);
  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop]);

  const showOffhand = !!(offhandArt && (hd || !merc));

  return (
    <D2ItemTooltip item={item} merc={merc} disabled={!!dragClass}>
      <div className={classNames(`d2p-slot d2p-slot-${slot}`, dragClass,
          {"d2p-empty": !item && !offhandArt, "d2p-active": active, "d2p-drag-copy": dragEffect})} {...handlers} ref={nodeRef}>
        <div className="d2p-slot-background"/>
        <div className="d2p-slot-highlight d2p-slot-rect"/>
        {item && <ItemImage className={classNames("d2p-icon", {"d2p-lowered": specialHl})} id={itemId} sockets={hovering}/>}
        {showOffhand && <ItemImage className="d2p-icon d2p-lowered" id={otherId}/>}
        {(showOffhand || brokenItem) && <div className="d2p-slot-disabled d2p-slot-rect"/>}
      </div>
    </D2ItemTooltip>
  );
}

function CubePopup({ paperdollRef, slotRef, onClose }) {
  const gameCtx = React.useContext(GameContext);
  const [style, setStyle] = React.useState(null);
  const nodeRef = React.useRef();

  React.useLayoutEffect(() => {
    setStyle(alignElement(nodeRef.current, slotRef.current.getBoundingClientRect(), [{from: 'br', to: 'bl'}, {from: 'bl', to: 'br'}], [0, 0]));
  }, [slotRef]);

  return (
    <Overlay>
      <div className={`d2p-CubePopup d2p-game-${gameCtx.name}`} style={style} ref={nodeRef}>
        <div className="d2p-close" onClick={onClose}/>
        <StorageGrid location="cube" paperdollRef={paperdollRef}/>
      </div>
    </Overlay>
  );
}

function StorageSlot({ location, index, id, active, dropCoords, dropBase, hideTooltip, paperdollRef }) {
  const [openBox, setOpenBox] = React.useState(false);
  const [hovering, setHovering] = React.useState(false);
  const [hlMerc, hlSlots] = EditingContext.useStateView("highlightItems") || [];

  const { gridSize } = React.useContext(GameContext);
  const { charmRows, [location]: { columns } } = Data.info;

  const item = Store.useItem(id) || presetItems[id];

  let specialHl = false;
  if (item && hlSlots && location === 'inventory' && hlSlots.has(`inventory${index}`) && !hlMerc) {
    active = true;
    specialHl = true;
  }

  const props = {
    onMouseEnter: React.useCallback(() => setHovering(true), []),
    onMouseLeave: React.useCallback(() => setHovering(false), []),
  };
  const y = Math.floor(index / columns);
  const x = index - y * columns;
  props.style = {
    left: x * gridSize,
    top: y * gridSize,
  };

  const aboveCharmLine = !!charmRows && y < charmRows;

  const brokenItem = Store.useSelector(state => {
    if (!item) return false;
    if (aboveCharmLine && itemGetTypes(item.base).has('char')) return true;
    return isItemUnusable(item, state, false);
  }, [item, charmRows, aboveCharmLine]);

  const base = Data.items[item?.base];
  const redhl = React.useMemo(() => {
    if (dropCoords && dropBase && base) {
      const [dx, dy] = dropCoords;
      const {invwidth: dw, invheight: dh} = dropBase;
      const {invwidth: w, invheight: h} = base;
      if (x + w > dx && x < dx + dw && y + h > dy && y < dy + dh) {
        const redhl = [];
        if (dx > x) redhl.push({left: 0, top: 0, right: (x + w - dx) * gridSize, bottom: 0});
        if (dx + dw < x + w) redhl.push({left: (dx + dw - x) * gridSize, top: 0, right: 0, bottom: 0});
        const left = Math.max(0, dx - x) * gridSize;
        const right = Math.max(0, x + w - dx - dw) * gridSize;
        if (dy > y) redhl.push({left, top: 0, right, bottom: (y + h - dy) * gridSize});
        if (dy + dh < y + h) redhl.push({left, top: (dy + dh - y) * gridSize, right, bottom: 0});
        return redhl;
      }
    }
  }, [base, dropBase, dropCoords, x, y, gridSize]);

  const ref = React.useRef();
  const onOpenBox = React.useCallback(e => {
    e.preventDefault();
    setOpenBox(open => !open);
  }, []);
  const onCloseBox = React.useCallback(() => setOpenBox(false), []);
  if (item?.base === 'box') {
    props.onContextMenu = onOpenBox;
  }

  return <>
    <D2ItemTooltip item={item} disabled={hideTooltip}>
      <div className={classNames("d2p-slot d2p-slot-inventory", {"d2p-active": active})} {...props} ref={ref}>
        {!redhl && <div className={classNames("d2p-slot-highlight d2p-slot-rect", {"d2p-red": brokenItem})}/>}
        {!!redhl && redhl.map((style, idx) => <div key={idx} className="d2p-slot-highlight d2p-red d2p-slot-rect" style={style}/>)}
        {item && <ItemImage className={classNames("d2p-icon", {"d2p-lowered": specialHl})} id={id} sockets={hovering}/>}
      </div>
    </D2ItemTooltip>
    {!!(item?.base === 'box' && openBox) && <CubePopup slotRef={ref} paperdollRef={paperdollRef} onClose={onCloseBox}/>}
  </>;
}

function StorageGrid({ location, paperdollRef }) {
  const active = EditingContext.useStateView("itemSlotEditing") === location;
  const [activeIndex, setActiveIndex] = EditingContext.useState(location + "SlotEditing", 0);
  const [dropPos, setDropPos] = React.useState();
  const store = Store.useStore();
  const storage = Store.useProfile(profile => profile[location]);
  const otherStorage = Store.useProfile(profile => profile[location === "cube" ? "inventory" : "cube"]);
  const editDispatch = EditingContext.useDispatch();
  const dragEffect = React.useContext(DragEffectContext);
  const gameCtx = React.useContext(GameContext);
  const { gridSize, [location]: storageSize } = gameCtx;
  const { columns } = storageSize;

  const dropCoords = dropPos?.split(';').map(x => parseInt(x));
  const dropIndex = dropCoords && (dropCoords[1] * columns + dropCoords[0]);

  let foundIndex = Store.useSelector(state => inventoryFindItem(storage, activeIndex, state, storageSize), [storage, activeIndex, storageSize]);
  if (foundIndex < 0) foundIndex = activeIndex;
  React.useEffect(() => {
    if (activeIndex !== foundIndex) {
      setActiveIndex(foundIndex);
    }
  }, [active, activeIndex, foundIndex, setActiveIndex]);

  const getSlotIndex = React.useCallback((clientX, clientY) => {
    const box = ref.current.getBoundingClientRect();
    const x = Math.floor((clientX - box.left) / gridSize);
    const y = Math.floor((clientY - box.top) / gridSize);
    const index = Math.max(0, Math.min(storageSize.columns - 1, x)) + Math.max(0, Math.min(storageSize.rows - 1, y)) * storageSize.columns;
    const foundIndex = inventoryFindItem(storage, index, store.getState(), storageSize);
    return foundIndex < 0 ? index : foundIndex;
  }, [store, storage, gridSize, storageSize]);

  const itemMover = useItemMover();
  const [dragClass, drag, drop, {dropObject}] = useDragDrop("ITEM", {
    //canDrag: () => false,
    item: monitor => {
      const offset = monitor.getInitialClientOffset();
      const index = getSlotIndex(offset.x, offset.y);
      return storage[index] && {type: "ITEM", id: storage[index], index, location};
    },
    end: (item, monitor) => itemMover({location, slot: item.index}, getDropResult(monitor, paperdollRef)),
  }, {
    canDrop: item => item.type === "ITEM" && !inventoryHasDuplicate(storage, item.id, store.getState(), item.index) &&
      !inventoryHasDuplicate(otherStorage, item.id, store.getState()) && (!location !== 'cube' || item.id !== 'box'),
    collect: monitor => ({dropObject: monitor.getItem()}),
    drop: () => ({location, slot: dropIndex}),
  }, [getSlotIndex, location, storage, store, dropIndex, otherStorage]);

  const ref = React.useRef();
  const nodeRef = React.useMemo(() => chainedRef(drag, drop, ref), [drag, drop]);

  const dropId = dropObject?.type === "ITEM" && dropObject.id;
  const dropItem = Store.useItem(dropId) || presetItems[dropId];
  const dropBase = Data.items[dropItem?.base];

  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();
  React.useEffect(() => dropBase && monitor.subscribeToOffsetChange(() => {
    const offset = monitor.getClientOffset();
    const box = ref.current.getBoundingClientRect();
    if (offset && offset.x >= box.left && offset.y >= box.top && offset.x < box.right && offset.y < box.bottom && dropBase) {
      let x = Math.round((offset.x - box.left) / gridSize - dropBase.invwidth / 2);
      let y = Math.round((offset.y - box.top) / gridSize - dropBase.invheight / 2);
      x = Math.max(0, Math.min(storageSize.columns - dropBase.invwidth, x));
      y = Math.max(0, Math.min(storageSize.rows - dropBase.invheight, y));
      setDropPos(`${x};${y}`);
    } else {
      setDropPos(null);
    }
  }), [monitor, dropBase, gridSize, storageSize]);

  const onClick = React.useCallback(e => {
    const index = getSlotIndex(e.clientX, e.clientY);
    setActiveIndex(index);
    editDispatch({type: "slot", slot: location, index});
    e.stopPropagation();
  }, [editDispatch, setActiveIndex, getSlotIndex, location]);

  return (
    <div className={classNames("d2p-InventoryGrid", dragClass, `d2p-${location}`)} onClick={onClick} ref={nodeRef}>
      {storage.map((id, index) => !!id && (dragEffect || index !== dropObject?.index || location !== dropObject?.location) && (
        <StorageSlot location={location} key={index} index={index} id={id} hideTooltip={!!dragClass}
          active={active && foundIndex === index} dropCoords={dropCoords} dropBase={dropBase} paperdollRef={paperdollRef}/>
      ))}
      {!!(active && !storage[foundIndex] && !(dropCoords && dropBase)) && <StorageSlot location={location} index={foundIndex} active={true} paperdollRef={paperdollRef}/>}
      {!!(dropCoords && dropBase) && (
        <div className="d2p-inventory-drop" style={{
          left: dropCoords[0] * gridSize,
          top: dropCoords[1] * gridSize,
          width: dropBase.invwidth * gridSize,
          height: dropBase.invheight * gridSize,
        }}/>
      )}
    </div>
  );
}

export const SwapButton = React.memo(function SwapButton({ className, onToggle }) {
  const [, ref] = useDrop({
    accept: 'ITEM',
    canDrop: () => false,
    hover: () => onToggle(),
  }, [onToggle]);

  return <div className={className} onClick={onToggle} ref={ref}/>;
});

export default React.memo(function Paperdoll() {
  const currentSlot = EditingContext.useStateView("itemSlotEditing");
  const gameCtx = React.useContext(GameContext);

  const isCC = useContentCreator();
  const profileId = useProfileSetId();

  const hasMerc = Store.useProfile(profile => !!profile.merc);
  const invSwap = Store.useProfile(profile => profile.weaponSet !== 0);

  const dispatch = Store.useDispatch();
  const onToggle = React.useCallback(() => dispatch.profile.setWeaponSet(set => (set === 0 ? 1 : 0)), [dispatch]);

  const ref = React.useRef();

  return (
    <div className={`d2p-Paperdoll d2p-game-${gameCtx.name}`} ref={ref}>
      <ClassSelect/>
      <div className={classNames("d2p-PaperdollChar", {"d2p-swapped": invSwap})}>
        <div className="d2p-invswap-1"/>
        <div className="d2p-invswap-2"/>
        <SwapButton className="d2p-invswap-btn1" onToggle={onToggle}/>
        <SwapButton className="d2p-invswap-btn2" onToggle={onToggle}/>

        {['tors', 'head', 'belt', 'glov', 'feet', 'rarm', 'larm', 'rarm2', 'larm2', 'neck', 'rrin', 'lrin'].map(slot => (
          <ItemSlot key={slot} id={slot} active={currentSlot === slot} paperdollRef={ref}/>
        ))}

        <StorageGrid location="inventory" paperdollRef={ref}/>
      </div>
      <MercSelect/>
      {hasMerc && (
        <div className="d2p-PaperdollMerc">
          {['merc_tors', 'merc_head', 'merc_rarm', 'merc_larm', Data.info.pd2 && 'merc_glov', Data.info.pd2 && 'merc_feet', Data.info.pd2 && 'merc_belt'].filter(Boolean).map(slot => (
            <ItemSlot key={slot} id={slot} active={currentSlot === slot} paperdollRef={ref}/>
          ))}
        </div>
      )}
      <AutoSave/>
      {!!(isCC && profileId) && (
        <div style={{marginTop: 2}}>
          <EmbedProfileLink {...profileId} type='player'>Player</EmbedProfileLink>
          {hasMerc && ' / '}
          {hasMerc && <EmbedProfileLink {...profileId} type='mercenary'>Mercenary</EmbedProfileLink>}
        </div>
      )}
    </div>
  );
});
